import ApiKeyDisplay from "@/components/Common/ApiKeyDisplay";
import { Button } from "@/components/ui/button";
import { useKeywordStore } from "@/features/keyword-monitoring/store/keywordStore";
import Navbar from "@/components/Common/Navbar";
import Contents from "@/features/documentation/components/Documentation/DocumentationContents";
import { type TokenWithMonitoring } from "@/features/auth/hooks/useApiToken";
import { useTokenValidation } from "@/hooks/useTokenValidation";

interface NavigationProps {
  isFirstTab: boolean;
  isLastTab: boolean;
  nextLabel: string;
  onNext: () => void;
  onBack: () => void;
}

export default function CardLauncher({
  onLaunch,
  requireStepValidation = true,
  showLaunchButton = true,
  customValidation,
  navigationProps,
  apiEndpoint,
  apiToken,
  isApiTokenLoading,
  contents,
  onSectionSelect,
  showTokenDropdown = false,
  onTokenSelect,
  selectedToken,
  availableTokens,
  tokensLoading,
}: {
  onLaunch?: () => void;
  requireStepValidation?: boolean;
  showLaunchButton?: boolean;
  customValidation?: () => boolean;
  navigationProps?: NavigationProps;
  apiEndpoint?: string;
  apiToken?: string;
  isApiTokenLoading?: boolean;
  contents?: boolean;
  onSectionSelect?: (sectionTitle: string) => void;
  showTokenDropdown?: boolean;
  onTokenSelect?: (token: TokenWithMonitoring) => void;
  selectedToken?: TokenWithMonitoring | null;
  availableTokens?: TokenWithMonitoring[];
  tokensLoading?: boolean;
}) {
  const selectedPlatforms = useKeywordStore((state) => state.selectedPlatforms);
  const dateRange = useKeywordStore((state) => state.dateRange);

  const { isLaunchDisabled: tokenValidationDisabled } = useTokenValidation({
    apiToken,
    selectedToken,
    showTokenDropdown,
  });

  const isDateRangeFilled = dateRange && dateRange.trim() !== "";
  const isPlatformsSelected = selectedPlatforms.length > 0;

  const isLaunchDisabled = customValidation
    ? !customValidation()
    : requireStepValidation
      ? !isDateRangeFilled || !isPlatformsSelected || tokenValidationDisabled
      : tokenValidationDisabled;

  return (
    <div className="flex h-full w-full flex-col justify-start gap-[20px]">
      <Navbar />

      <div className="bg-brand-100 flex h-auto w-[280px] flex-col gap-[20px] rounded-xl px-3 py-4">
        {!contents ? (
          <div className="flex flex-col gap-2">
            <ApiKeyDisplay
              label="API Key"
              value={apiToken ?? ""}
              type="api"
              isLoading={isApiTokenLoading || tokensLoading}
              showTokenDropdown={showTokenDropdown}
              selectedToken={selectedToken}
              onTokenSelect={onTokenSelect}
              availableTokens={availableTokens}
            />
            {apiEndpoint && (
              <ApiKeyDisplay
                label="Endpoint"
                value={apiEndpoint ?? ""}
                type="endpoint"
              />
            )}
          </div>
        ) : (
          <div>
            <Contents onSectionSelect={onSectionSelect} />
          </div>
        )}
      </div>

      {showLaunchButton && (
        <div className="flex w-full flex-shrink-0 flex-col justify-center gap-3">
          {navigationProps ? (
            <>
              {!navigationProps.isFirstTab && (
                <Button
                  variant="outline"
                  icon
                  onClick={navigationProps.onBack}
                  className="h-[48px]"
                >
                  Back
                </Button>
              )}

              {!navigationProps.isLastTab ? (
                <Button
                  variant="default"
                  icon
                  onClick={navigationProps.onNext}
                  className="h-[48px]"
                >
                  Next: {navigationProps.nextLabel}
                </Button>
              ) : (
                <Button
                  variant="default"
                  icon
                  disabled={isLaunchDisabled}
                  onClick={onLaunch}
                  className="h-[48px]"
                >
                  <i className="ri-arrow-right-s-fill text-[18px]"></i>
                  Launch
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="default"
              icon
              disabled={isLaunchDisabled}
              onClick={onLaunch}
              className="h-[48px]"
            >
              <i className="ri-arrow-right-s-fill text-[18px]"></i>
              Launch
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
